import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ShieldCheck, Plus, Trash2, FileKey, Award, Globe, Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Certs() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("self_signed");

  // self-signed form
  const [ss, setSs] = useState({ common_name: "api.rustwall.local", san: "", days: 365, organization: "RustWall" });
  // upload form
  const [up, setUp] = useState({ name: "", cert_pem: "", key_pem: "", chain_pem: "" });
  // acme form
  const [ac, setAc] = useState({ common_name: "", san: "", email: "", directory_url: "https://acme-v02.api.letsencrypt.org/directory", staging: true });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/certs"); setItems(data); }
    catch (err) { console.error("certs load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createSelfSigned = async () => {
    try {
      const body = { ...ss, san: ss.san ? ss.san.split(",").map((s) => s.trim()).filter(Boolean) : [] };
      await api.post("/certs/self-signed", body);
      toast.success("Self-signed certificate generated");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const upload = async () => {
    try {
      await api.post("/certs/upload", up);
      toast.success("Certificate uploaded");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const acme = async () => {
    try {
      const body = { ...ac, san: ac.san ? ac.san.split(",").map((s) => s.trim()).filter(Boolean) : [] };
      // Use the real ACME issuance endpoint; falls back gracefully on failure
      const { data } = await api.post("/certs/acme/issue", body);
      toast.success(`Certificate issued for ${data.common_name}`);
      setOpen(false); load();
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      toast.error(detail || "ACME issuance failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this certificate?")) return;
    await api.delete(`/certs/${id}`); toast.success("Removed"); load();
  };

  return (
    <Layout title="Certificate Management" subtitle="TLS / X.509">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="certs-count">
          {items.length} CERTIFICATES · {items.filter((c) => c.days_remaining < 30 && c.days_remaining >= 0).length} EXPIRING SOON
        </div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="cert-new-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2">
                <Plus className="w-4 h-4" /> NEW CERTIFICATE
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FileKey className="w-4 h-4 text-cyan-400" /> Issue / Import Certificate</DialogTitle></DialogHeader>
              <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="grid grid-cols-3 bg-zinc-900 rounded-sm" data-testid="cert-tabs">
                  <TabsTrigger value="self_signed" data-testid="tab-self-signed">SELF-SIGNED</TabsTrigger>
                  <TabsTrigger value="upload" data-testid="tab-upload">UPLOAD</TabsTrigger>
                  <TabsTrigger value="acme" data-testid="tab-acme">ACME / LE</TabsTrigger>
                </TabsList>

                <TabsContent value="self_signed" className="space-y-3 pt-3">
                  <div><L>Common Name</L><Input data-testid="ss-cn" value={ss.common_name} onChange={(e) => setSs({ ...ss, common_name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                  <div><L>SAN (comma-separated DNS names)</L><Input data-testid="ss-san" value={ss.san} onChange={(e) => setSs({ ...ss, san: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" placeholder="*.rustwall.local" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><L>Validity (days)</L><Input data-testid="ss-days" type="number" value={ss.days} onChange={(e) => setSs({ ...ss, days: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                    <div><L>Organization</L><Input data-testid="ss-org" value={ss.organization} onChange={(e) => setSs({ ...ss, organization: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
                  </div>
                  <DialogFooter><Button data-testid="ss-create" onClick={createSelfSigned} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">GENERATE</Button></DialogFooter>
                </TabsContent>

                <TabsContent value="upload" className="space-y-3 pt-3">
                  <div><L>Name</L><Input data-testid="up-name" value={up.name} onChange={(e) => setUp({ ...up, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
                  <div><L>Certificate (PEM)</L><Textarea data-testid="up-cert" value={up.cert_pem} onChange={(e) => setUp({ ...up, cert_pem: e.target.value })} rows={5} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-xs" placeholder="-----BEGIN CERTIFICATE-----" /></div>
                  <div><L>Private Key (PEM)</L><Textarea data-testid="up-key" value={up.key_pem} onChange={(e) => setUp({ ...up, key_pem: e.target.value })} rows={5} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-xs" placeholder="-----BEGIN PRIVATE KEY-----" /></div>
                  <div><L>Chain (optional)</L><Textarea data-testid="up-chain" value={up.chain_pem} onChange={(e) => setUp({ ...up, chain_pem: e.target.value })} rows={3} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-xs" /></div>
                  <DialogFooter><Button data-testid="up-submit" onClick={upload} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><UploadIcon className="w-4 h-4" />UPLOAD</Button></DialogFooter>
                </TabsContent>

                <TabsContent value="acme" className="space-y-3 pt-3">
                  <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 text-[11px] font-mono text-amber-300">
                    <strong>Real Let's Encrypt issuance.</strong> Requires:
                    (1) public DNS for the CN/SAN pointing to this host,
                    (2) port 80 reachable from the internet for HTTP-01.
                    In preview environments this typically fails at challenge validation —
                    deploy with a real domain + ingress routing /.well-known/acme-challenge/* to backend.
                  </div>
                  <div><L>Common Name</L><Input data-testid="ac-cn" value={ac.common_name} onChange={(e) => setAc({ ...ac, common_name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" placeholder="api.example.com" /></div>
                  <div><L>SAN</L><Input data-testid="ac-san" value={ac.san} onChange={(e) => setAc({ ...ac, san: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
                  <div><L>Account Email</L><Input data-testid="ac-email" type="email" value={ac.email} onChange={(e) => setAc({ ...ac, email: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
                  <div><L>Directory URL</L><Input data-testid="ac-dir" value={ac.directory_url} onChange={(e) => setAc({ ...ac, directory_url: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono text-xs" /></div>
                  <div className="flex items-center gap-2 text-xs font-mono text-zinc-400">
                    <input type="checkbox" data-testid="ac-staging" checked={!!ac.staging} onChange={(e) => setAc({ ...ac, staging: e.target.checked })} className="accent-cyan-500" />
                    Use Let's Encrypt <span className="text-amber-400">staging</span> (recommended for testing)
                  </div>
                  <DialogFooter><Button data-testid="ac-submit" onClick={acme} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Globe className="w-4 h-4" />ISSUE</Button></DialogFooter>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="certs-list">
        {items.map((c) => {
          const expSoon = c.days_remaining < 30 && c.days_remaining >= 0;
          const expired = c.days_remaining < 0;
          return (
            <div key={c.cert_id} data-testid={`cert-card-${c.cert_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display text-lg font-bold tracking-tight">{c.common_name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{c.cert_id} · {c.source}</div>
                </div>
                <span className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm flex items-center gap-1.5 " +
                  (expired ? "text-red-400 border-red-500/40 bg-red-500/10"
                    : expSoon ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
                    : "text-emerald-400 border-emerald-500/40 bg-emerald-500/10")}>
                  <ShieldCheck className="w-3 h-3" />
                  {expired ? "EXPIRED" : `${c.days_remaining}d LEFT`}
                </span>
              </div>
              <div className="font-mono text-[11px] space-y-1 text-zinc-300">
                <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">ISSUER</span>{c.issuer}</div>
                <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">SAN</span>{(c.san || []).join(", ") || "—"}</div>
                <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">VALID</span>{c.not_before?.slice(0, 10)} → {c.not_after?.slice(0, 10)}</div>
                <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">SHA256</span><span className="text-cyan-400 break-all text-[10px]">{c.fingerprint_sha256}</span></div>
              </div>
              {user?.role === "admin" && (
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-zinc-800">
                  <Button size="sm" variant="ghost" data-testid={`cert-delete-${c.cert_id}`} onClick={() => del(c.cert_id)} className="gap-1.5 text-red-400"><Trash2 className="w-3.5 h-3.5" />Revoke</Button>
                  <span className="ml-auto text-[10px] font-mono text-zinc-500"><Award className="inline w-3 h-3 mr-1" />by {c.created_by}</span>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no certificates yet.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
